// AC GUIDE 6: HOW MUCH DOES AC INSTALLATION COST IN DELHI? (20 Oct 2026)
// /blog/ac-installation-cost
//
// Written to GUIDES_PLAYBOOK.md section 3. The answer comes from Citi Cool's
// own rate card (RATES in data/pricing.js, before GST; GST_RATE added, as the
// quote does) and Saheb's rules for it: installation is a choice, not a
// default; a nitrogen pressure test on every install; copper, drain and
// wiring are three runs of different lengths, each measured on site; removal
// of the old AC is free with an exchange. What a standard install includes
// comes from Daikin India's own scope document and FAQ.
//
// NO RUPEES TYPED HERE. Callers pass RATES, GST_RATE, EXCHANGE and
// KIT_COPPER_METRES from data/pricing.js, so a change to the rate card moves
// this page by itself. No imports: prerender.js loads this file by stripping
// `export`. **double stars** mean bold.

export const INSTALL_SLUG = 'blog/ac-installation-cost';
export const INSTALL_PUBLISHED = '2026-10-20';
export const INSTALL_UPDATED = '2026-10-20';

export const INSTALL_SEO = {
  title: 'AC Installation Cost in Delhi: Split AC Charges, Item by Item | Citi Cool',
  meta: 'How much does split AC installation cost in Delhi? About ₹2,700 to fit it, plus about ₹1,500 a metre of copper pipe. Our full rate card, with GST.',
  eyebrow: 'AC Guide · Installation',
  h1: 'How much does AC installation cost in Delhi?',
  crumb: 'AC installation cost',
  readMins: 4,
};

// The worked example in the animation: a split AC with 3 m of each run and a stand.
export const EXAMPLE = { copperM: 3, drainM: 3, wireM: 3, stand: true };
export const TOOL_COPPER = [3, 5, 8];

// ─── sources ──────────────────────────────────────────────────────────────

export const INSTALL_SOURCES = [
  { key: 'ratecard', label: 'Our installation rate card: rates before GST, with 18% GST added as its own line on the quote (October 2026)', by: 'Citi Cool', href: null },
  { key: 'saheb', label: 'How we quote an install: installation is your choice, a pressure test on every install, and copper, drain and wiring each measured on site (October 2026)', by: 'Citi Cool', href: null },
  { key: 'exchange', label: 'Exchange your old AC: up to ₹7,000 off, with free removal of the old unit', by: 'Citi Cool', href: '/daikin-ac-price-list-delhi' },
  { key: 'team', label: 'Sales and service under one roof: our own team installs and services every AC we sell', by: 'Citi Cool', href: null },
  { key: 'daikin-install', label: 'What is covered in a standard AC installation scope (split AC up to 2.6 TR)', by: 'Daikin India', href: 'https://daikinindia.com/sites/default/files/daikin-standard-installation.pdf' },
  { key: 'daikin-faq', label: 'FAQs: the outdoor unit package includes 3 m of copper piping; extra length is charged', by: 'Daikin India', href: 'https://daikinindia.com/faqs' },
];

// ─── arithmetic ───────────────────────────────────────────────────────────

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const inr100 = (n) => `₹${(Math.round(n / 100) * 100).toLocaleString('en-IN')}`;

// Every rate with GST, from RATES (before GST) and GST_RATE.
export function withGst(rates, gst) {
  const g = (k) => rates[k].price * (1 + gst);
  return {
    fit: g('installSplit'),
    test: g('nitrogenTest'),
    copper: g('copperSplit'),
    drain: g('drainPipe'),
    wire: g('controlWiring'),
    stand: g('standSplit15'),
    remove: g('dismantleOld'),
    chisel: g('chiseling'),
    coat: g('antiCorrosion'),
  };
}

// One job, line by line, with GST. job: { copperM, drainM, wireM, stand, remove }.
export function bill(r, job) {
  const lines = [
    { key: 'fit', label: 'Fitting the AC', amount: r.fit },
    { key: 'test', label: 'Nitrogen pressure test', amount: r.test },
    { key: 'copper', label: `Copper pipe, ${job.copperM} m`, amount: r.copper * job.copperM },
  ];
  if (job.drainM) lines.push({ key: 'drain', label: `Drain pipe, ${job.drainM} m`, amount: r.drain * job.drainM });
  if (job.wireM) lines.push({ key: 'wire', label: `Wiring, ${job.wireM} m`, amount: r.wire * job.wireM });
  if (job.stand) lines.push({ key: 'stand', label: 'Stand for the outdoor unit', amount: r.stand });
  if (job.remove) lines.push({ key: 'remove', label: 'Removing the old AC', amount: r.remove });
  return { lines, total: lines.reduce((a, l) => a + l.amount, 0) };
}

// The tool's sentence. old: 'none' | 'remove' | 'exchange'.
export function toolText(r, copperM, stand, old) {
  const b = bill(r, { copperM, stand, remove: old === 'remove' });
  const parts = ['fitting', 'the test', `${copperM} m of copper`];
  if (stand) parts.push('a stand');
  if (old === 'remove') parts.push('removing your old AC');
  const list = `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `For ${list}, expect about **${inr100(b.total)}** with GST. Drain pipe and wiring add about ${inr100(r.drain)} a metre each${old === 'exchange' ? '. Removal is free with your exchange.' : '.'}`;
}

// ─── the page ─────────────────────────────────────────────────────────────

export function installContent(rates, gst, exchange, kitM) {
  const r = withGst(rates, gst);
  const ex = bill(r, EXAMPLE);
  const exMax = Math.max(...exchange.copper.map((c) => c.value));
  const gstPct = Math.round(gst * 100);

  return {
    answer: {
      text: `**Expect about ${inr100(r.fit + r.test)} to fit a split AC. Each metre of copper pipe adds about ${inr100(r.copper)}.** Prices include GST.`,
      src: ['ratecard'],
    },
    because: [
      { text: `The fitting is ${inr(rates.installSplit.price)}, plus a ${inr(rates.nitrogenTest.price)} pressure test, before GST. We do the test on every install.`, src: ['ratecard', 'saheb'] },
      { text: 'Copper pipe, drain pipe and wiring are charged by the metre. Each is measured on site.', src: ['ratecard', 'saheb'] },
      { text: `A stand and removing your old AC cost extra. Removal is free when you exchange your old AC.`, src: ['ratecard', 'exchange'] },
    ],
    watch: {
      title: 'Watch: how an AC installation bill adds up',
      captions: [
        { at: 0, text: `First, the fitting. Both units go up and are joined. That is about ${inr100(r.fit)}.` },
        { at: 0.22, text: `Next, a nitrogen pressure test, and the pipes are flushed. About ${inr100(r.test)}.` },
        { at: 0.42, text: `Then the copper pipe, metre by metre. Each metre adds about ${inr100(r.copper)}.` },
        { at: 0.7, text: `Add the drain pipe, the wiring and a stand. Here the total is about ${inr100(ex.total)}.` },
      ],
      note: `An example with ${EXAMPLE.copperM} m each of copper, drain pipe and wiring, and a stand. Our rates, with ${gstPct}% GST.`,
      noteSrc: ['ratecard'],
    },
    table: {
      title: 'AC installation charges in Delhi: our rate card',
      head: ['Item', 'Price with GST'],
      rows: [
        ['Fitting a split AC', inr(r.fit)],
        ['Nitrogen pressure test (every install)', inr(r.test)],
        ['Copper pipe, with insulation', `${inr(r.copper)} a metre`],
        ['Drain pipe, or wiring', `${inr(r.drain)} a metre each`],
        ['Stand for a 1.5 to 2 ton AC', inr(r.stand)],
        ['Removing an old AC', `${inr(r.remove)} (free with exchange)`],
      ],
      note: `Before GST, the fitting is ${inr(rates.installSplit.price)} and copper is ${inr(rates.copperSplit.price)} a metre. The quote adds ${gstPct}% GST as its own line.`,
      src: ['ratecard', 'exchange'],
    },
    sides: [
      {
        key: 'dos',
        title: 'AC installation: do\'s and don\'ts',
        goodHead: 'Do',
        badHead: 'Don\'t',
        good: [
          { text: 'Get each pipe run measured on site. Then you pay only for what is used.', src: ['saheb'] },
          { text: `Exchange your old AC. Removal is free, and you get up to ${inr(exMax)} off.`, src: ['exchange'] },
          { text: `Check if your AC has Daikin's piping kit. It has ${kitM} metres of copper in the box.`, src: ['daikin-faq'] },
          { text: 'Keep the outdoor unit where it is safe to service.', src: ['daikin-install'] },
          { text: 'Pick the [right size AC](/blog/what-size-ac-do-i-need) for your room first.', src: [] },
        ],
        bad: [
          { text: 'Don\'t skip the pressure test. We do it on every install.', src: ['saheb'] },
          { text: 'Don\'t use one length for all three runs. Copper, drain and wiring rarely match.', src: ['saheb'] },
          { text: 'Don\'t forget the power point. Daikin\'s standard install leaves the wiring from the meter to you.', src: ['daikin-install'] },
        ],
      },
    ],
    parts: [
      {
        id: 'included',
        q: 'What is included in AC installation?',
        bullets: [
          { text: 'The indoor unit goes on the wall. The outdoor unit goes in a safe spot outside.', src: ['daikin-install'] },
          { text: 'No space for an outdoor unit for every room? Read [VRV or split AC](/blog/vrv-vs-split-ac).', src: [] },
          { text: 'One hole is drilled in a brick wall for the pipes.', src: ['daikin-install'] },
          { text: 'The units are joined, checked and shown to you working.', src: ['daikin-install'] },
        ],
      },
      {
        id: 'copper',
        q: 'Why is copper pipe charged by the metre?',
        bullets: [
          { text: 'The copper pipe joins the indoor and outdoor units. Its length depends on your home.', src: ['daikin-faq'] },
          { text: 'We measure it on site. So you pay only for the pipe that is used.', src: ['saheb'] },
          { text: `An AC sold with Daikin's kit has ${kitM} metres of copper in the box. You pay only for copper beyond that.`, src: ['daikin-faq'] },
        ],
      },
      {
        id: 'extra',
        q: 'What costs extra in AC installation?',
        bullets: [
          { text: `A stand for the outdoor unit: about ${inr100(r.stand)}.`, src: ['ratecard'] },
          { text: `Chiseling the wall: about ${inr100(r.chisel)} a metre.`, src: ['ratecard'] },
          { text: `Anti-corrosion treatment, for places prone to rust: about ${inr100(r.coat)}.`, src: ['ratecard', 'daikin-install'] },
          { text: 'Cutting through a thick concrete wall costs extra too, says Daikin.', src: ['daikin-install'] },
        ],
      },
      {
        id: 'online',
        q: 'Is installation included when I buy an AC online?',
        bullets: [
          { text: 'No. Installation is your choice. You can add it to your order.', src: ['saheb'] },
          { text: 'We check your site and give you the final quote after your order.', src: ['ratecard'] },
          { text: 'Our own team fits and services every AC we sell.', src: ['team'] },
        ],
      },
    ],
    faq: [
      {
        q: 'What is the installation charge for a split AC in Delhi?',
        a: `Our fitting charge is ${inr(r.fit)} with GST. With the pressure test, it is about ${inr100(r.fit + r.test)}.`,
        src: ['ratecard'],
      },
      {
        q: 'How much does AC copper pipe cost per metre?',
        a: `${inr(rates.copperSplit.price)} a metre before GST, about ${inr100(r.copper)} with GST. That includes the insulation.`,
        src: ['ratecard'],
      },
      {
        q: 'How much does it cost to uninstall an AC?',
        a: `About ${inr100(r.remove)} with GST. It is free when you exchange the old AC with us.`,
        src: ['ratecard', 'exchange'],
      },
      {
        q: 'Is the AC stand included in installation?',
        a: `No. A stand for a 1.5 to 2 ton AC costs ${inr(r.stand)} with GST.`,
        src: ['ratecard'],
      },
      {
        q: 'Is GST charged on AC installation?',
        a: `Yes, ${gstPct}%. Our rate card shows prices before GST. The quote adds GST as its own line.`,
        src: ['ratecard'],
      },
      {
        q: 'How much does a whole AC installation cost?',
        a: `It depends on your pipe runs. With ${EXAMPLE.copperM} metres of each and a stand, about ${inr100(ex.total)} with GST.`,
        src: ['ratecard'],
      },
    ],
    tool: {
      title: 'Work out your installation cost',
      examples: TOOL_COPPER.map((m) => toolText(r, m, true, 'none')),
    },
  };
}
